import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import UpcomingBookingCard from './components/UpcomingBookingCard';
import BookingHistoryTable from './components/BookingHistoryTable';
import RecommendationGrid from './components/RecommendationGrid';
import UserStatsCard from './components/UserStatsCard';
import QuickActionsPanel from './components/QuickActionsPanel';
import { useAuth } from '../../contexts/AuthContext';
import { useUserBookings } from '../../hooks/useUserBookings';

const UserDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { user, userProfile, loading: authLoading } = useAuth();
  const { 
    bookings, 
    loading, 
    error, 
    getUpcomingBookings, 
    getPastBookings 
  } = useUserBookings();

  const upcomingBookings = getUpcomingBookings();
  const bookingHistory = getPastBookings();

  // No need for manual data fetching - handled by useUserBookings hook

  // Mock user data
  const mockUser = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
    memberSince: "2023-01-15",
    preferences: ["Technology", "Music", "Art", "Business"]
  };

  // Mock upcoming bookings
  const mockUpcomingBookings = [
    {
      id: 1,
      eventId: "evt_001",
      eventTitle: "Tech Innovation Summit 2025",
      eventImage: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop",
      eventDate: "2025-01-15T09:00:00Z",
      venue: "San Francisco Convention Center",
      ticketType: "VIP Access",
      quantity: 2,
      totalAmount: 299.99,
      status: "confirmed",
      qrCodeUrl: "/tickets/qr/evt_001"
    },
    {
      id: 2,
      eventId: "evt_002",
      eventTitle: "Jazz Under the Stars",
      eventImage: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop",
      eventDate: "2025-01-20T19:30:00Z",
      venue: "Rooftop Garden Venue",
      ticketType: "General Admission",
      quantity: 1,
      totalAmount: 45.00,
      status: "confirmed",
      qrCodeUrl: "/tickets/qr/evt_002"
    },
    {
      id: 3,
      eventId: "evt_003",
      eventTitle: "Digital Art Exhibition Opening",
      eventImage: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop",
      eventDate: "2025-01-25T18:00:00Z",
      venue: "Modern Art Gallery",
      ticketType: "Early Bird",
      quantity: 1,
      totalAmount: 25.00,
      status: "pending",
      qrCodeUrl: "/tickets/qr/evt_003"
    }
  ];

  // Mock booking history
  const mockBookingHistory = [
    {
      id: 101,
      eventId: "evt_101",
      eventTitle: "Web Development Bootcamp",
      eventImage: "https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?w=400&h=300&fit=crop",
      eventDate: "2024-12-10T10:00:00Z",
      venue: "Tech Hub Downtown",
      ticketType: "Full Access",
      quantity: 1,
      totalAmount: 149.99,
      status: "attended",
      receiptUrl: "/receipts/evt_101.pdf",
      canReview: true
    },
    {
      id: 102,
      eventId: "evt_102",
      eventTitle: "Classical Music Concert",
      eventImage: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop",
      eventDate: "2024-11-28T20:00:00Z",
      venue: "Symphony Hall",
      ticketType: "Premium Seating",
      quantity: 2,
      totalAmount: 180.00,
      status: "attended",
      receiptUrl: "/receipts/evt_102.pdf",
      canReview: true
    },
    {
      id: 103,
      eventId: "evt_103",
      eventTitle: "Startup Pitch Competition",
      eventImage: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=300&fit=crop",
      eventDate: "2024-11-15T14:00:00Z",
      venue: "Innovation Center",
      ticketType: "Networking Pass",
      quantity: 1,
      totalAmount: 75.00,
      status: "attended",
      receiptUrl: "/receipts/evt_103.pdf",
      canReview: false
    }
  ];

  // Mock recommendations (these would typically come from a recommendation API)
  const mockRecommendations = [
    {
      id: "rec_001",
      title: "AI & Machine Learning Conference",
      image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&h=300&fit=crop",
      date: "2025-02-10T09:00:00Z",
      time: "9:00 AM",
      location: "Tech Convention Center",
      price: 199,
      rating: 4.8,
      attendees: 1250,
      tags: ["Technology", "AI", "Conference"],
      isPopular: true
    },
    {
      id: "rec_002",
      title: "Local Food & Wine Festival",
      image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop",
      date: "2025-02-14T12:00:00Z",
      time: "12:00 PM",
      location: "Riverside Park",
      price: 0,
      rating: 4.6,
      attendees: 850,
      tags: ["Food", "Festival", "Community"],
      isPopular: false
    },
    {
      id: "rec_003",
      title: "Contemporary Art Workshop",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop",
      date: "2025-02-18T14:00:00Z",
      time: "2:00 PM",
      location: "Creative Arts Studio",
      price: 65,
      rating: 4.9,
      attendees: 45,
      tags: ["Art", "Workshop", "Creative"],
      isPopular: false
    }
  ];

  // Calculate user stats from real data
  const userStats = {
    eventsAttended: bookingHistory?.length || 0,
    upcomingBookings: upcomingBookings?.length || 0,
    totalSpent: bookingHistory?.reduce((total, booking) => 
      total + (booking?.total_amount || 0), 0) || 0,
    favoriteVenues: new Set(
      [...upcomingBookings, ...bookingHistory]?.map(booking => 
        booking?.events?.venue_name
      )?.filter(Boolean)
    )?.size || 0
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Icon name="AlertCircle" size={32} className="mx-auto mb-4 text-destructive" />
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => window.location?.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />
      
      {/* Dashboard Header */}
      <div className="pt-16 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <img
                src={userProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.full_name || 'User')}&background=6366f1&color=ffffff`}
                alt={userProfile?.full_name || 'User'}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  Welcome back, {userProfile?.full_name?.split(' ')?.[0] || 'User'}!
                </h1>
                <p className="text-muted-foreground">
                  Member since {userProfile?.created_at ? new Date(userProfile.created_at)?.toLocaleDateString('en-US', { 
                    month: 'long', 
                    year: 'numeric' 
                  }) : 'Recently'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm">
                <Icon name="Settings" size={16} className="mr-2" />
                Settings
              </Button>
              <Button variant="default" size="sm" asChild>
                <Link to="/event-discovery">
                  <Icon name="Plus" size={16} className="mr-2" />
                  Book Event
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Tabs */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
              { id: 'bookings', label: 'My Bookings', icon: 'Calendar' },
              { id: 'history', label: 'History', icon: 'Clock' },
              { id: 'recommendations', label: 'For You', icon: 'Sparkles' }
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <UserStatsCard stats={userStats} />

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Upcoming Bookings */}
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Upcoming Events</h2>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('bookings')}>
                      View All
                      <Icon name="ArrowRight" size={14} className="ml-2" />
                    </Button>
                  </div>
                  
                  {upcomingBookings?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {upcomingBookings?.slice(0, 4)?.map((booking) => (
                        <UpcomingBookingCard key={booking?.id} booking={booking} />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-card border border-border rounded-lg p-8 text-center">
                      <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No upcoming events</h3>
                      <p className="text-muted-foreground mb-4">
                        Discover amazing events happening near you
                      </p>
                      <Button variant="default" asChild>
                        <Link to="/event-discovery">
                          <Icon name="Search" size={16} className="mr-2" />
                          Explore Events
                        </Link>
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Quick Actions */}
              <div className="space-y-6">
                <QuickActionsPanel upcomingBookings={upcomingBookings} />
              </div>
            </div>

            {/* Recommendations */}
            <RecommendationGrid recommendations={mockRecommendations?.slice(0, 3)} />
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-foreground">My Bookings</h2>
              <Button variant="default" asChild>
                <Link to="/event-discovery">
                  <Icon name="Plus" size={16} className="mr-2" />
                  Book New Event
                </Link>
              </Button>
            </div>
            
            {upcomingBookings?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingBookings?.map((booking) => (
                  <UpcomingBookingCard key={booking?.id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Calendar" size={64} className="mx-auto text-muted-foreground mb-6" />
                <h3 className="text-xl font-medium text-foreground mb-3">No upcoming bookings</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Start exploring events and book your next amazing experience
                </p>
                <Button variant="default" size="lg" asChild>
                  <Link to="/event-discovery">
                    <Icon name="Search" size={20} className="mr-2" />
                    Discover Events
                  </Link>
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-foreground">Booking History</h2>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm">
                  <Icon name="Download" size={16} className="mr-2" />
                  Export
                </Button>
                <Button variant="outline" size="sm">
                  <Icon name="Filter" size={16} className="mr-2" />
                  Filter
                </Button>
              </div>
            </div>
            
            <BookingHistoryTable bookings={bookingHistory} />
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Recommended for You</h2>
                <p className="text-muted-foreground mt-1">
                  Curated events based on your interests and preferences
                </p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/event-discovery">
                  <Icon name="Settings" size={16} className="mr-2" />
                  Update Preferences
                </Link>
              </Button>
            </div>
            
            <RecommendationGrid recommendations={mockRecommendations} />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;